import { useAlertDetailStore } from '@/alert/stores/alert-detail';
import { ALERT_INSIGHT_DATA, AlertInsightData } from '@/alert/utils/alertInsight.define';
import { getDuringTimeItems, getEvaluateValueItems } from '@/alert/utils/define';
import { getDisplayNameFromSelectboxItem } from '@/alert/utils/utils';
import {
  type AlertRuleDetailV9,
  CurrentAlertConditionValueItem,
  TargetItem,
} from '@/api-client/alert/model';
import { GridOptionV2 } from '@/common/components/molecules/modernGrid/modernGrid.types';
import { useModernGridHelper } from '@/common/components/molecules/modernGrid/modernGrid.uses';
import { useInternational } from '@/common/locale';
import { useStatInfoStore } from '@/common/stores/stat-info';
import { CustomColumn, DetailInfoData } from '@/common/utils';
import { cloneDeep } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { ref, computed, watch } from 'vue';
import { RuleInfoProps } from './alertDetail.types';
import { AlertTargetItem } from '../alertTargetList/alertTargetList.types';

export interface Props {
  isShow: boolean;
}

interface ThresholdDisplay {
  conditionType: string;
  evaluateValue: string;
  duringTimeInterval: string;
  comparator: string;
  values: { warning: string; critical: string };
  unit: string;
}

const getTargets = ({
  targets,
  targetId,
  category,
}: {
  targets?: TargetItem[];
  targetId?: string;
  category?: string;
}): AlertTargetItem[] => {
  let targetList: TargetItem[] = [];
  if (!targetId) targetList = targets ?? [];
  else {
    const target = targets?.find((target) => target.targetId === targetId);
    targetList = target ? [target] : [{ category, targetId, targetName: targetId }];
  }

  return targetList.map((target) => ({
    name: target.targetName ? `${target.category}: ${target.targetName}` : '',
    id: target.targetId,
    category: target.category,
    clickable: true,
    alias: target.targetAlias,
  }));
};

const getTypeLabel = (type: string | undefined, t: (key: string) => string): string =>
  type ? `${t(`WORD.${type.toUpperCase()}`)} ${t('WORD.ALERT')}` : '';

const formatThresholdForDisplay = (
  metricThreshold: CurrentAlertConditionValueItem | undefined,
  metricUnit: string | undefined,
): ThresholdDisplay => ({
  conditionType: metricThreshold?.conditionType ?? '',
  evaluateValue: getDisplayNameFromSelectboxItem(
    getEvaluateValueItems(),
    metricThreshold?.evaluateValue,
  ),
  duringTimeInterval: getDisplayNameFromSelectboxItem(
    getDuringTimeItems(),
    metricThreshold?.duringTimeInterval,
  ),
  comparator: metricThreshold?.criticalComparison ?? '',
  values: {
    warning: String(metricThreshold?.warning ?? ''),
    critical: String(metricThreshold?.critical ?? ''),
  },
  unit: metricUnit ?? '',
});

const formatAlertInfoItems = (
  template: DetailInfoData[],
  valueByField: Record<string, string[] | unknown[]>,
): DetailInfoData[] =>
  template.map((item) => ({
    ...item,
    values: (valueByField[item.field] ?? []) as string[],
  }));

export const setup = (props: Props) => {
  const { t, locale } = useInternational();
  const { selectedRuleInfo, userAlertInformationData } = storeToRefs(useAlertDetailStore());
  const statInfoStore = useStatInfoStore();

  const data = ref<AlertInsightData | null>(null);

  const DEFAULT_ALERT_INFO: DetailInfoData[] = [
    { label: t('WORD.ALERT_NAME'), field: 'alertName', values: [], renderType: 'text' },
    { label: t('WORD.TARGET'), field: 'target', values: [], renderType: 'customValue' },
    { label: t('WORD.TYPE2'), field: 'type', values: [], renderType: 'text' },
  ];

  const DEFAULT_ALERT_INFO_EVENT: DetailInfoData[] = [
    { label: t('WORD.EVENT_CODE'), field: 'eventCode', values: [], renderType: 'text' },
    { label: t('WORD.SEVERITY'), field: 'severity', values: [], renderType: 'status' },
  ];

  const DEFAULT_ALERT_INFO_METRIC: DetailInfoData[] = [
    { label: t('WORD.METRIC'), field: 'metric', values: [], renderType: 'text' },
    { label: t('WORD.THRESHOLD'), field: 'threshold', values: [], renderType: 'customValue' },
  ];

  const alertInfo = ref<DetailInfoData[]>(DEFAULT_ALERT_INFO);

  const columnsData: CustomColumn<string>[] = [
    { caption: t('WORD.CAUSE'), field: 'cause', type: 'string', width: 180 },
    {
      caption: t('WORD.PRIORITY'),
      field: 'priority',
      type: 'string',
      width: 90,
      rendererType: 'capitalize-label',
    },
    { caption: t('WORD.DESCRIPTION'), field: 'explanation', type: 'string' },
    { caption: t('WORD.DIAGNOSTIC_CRITERIA'), field: 'diagnosticCriteria', type: 'string' },
  ];

  const { transformColumns } = useModernGridHelper();
  const columns = computed(() => transformColumns(columnsData));

  const rows = ref<AlertInsightData['mainCauses']>([]);
  const option: GridOptionV2 = {
    tokenFilter: { use: false },
    gridSetting: {
      use: true,
      exportExcelOption: { title: t('WORD.PRIMARY_CAUSES') },
    },
  };

  const init = () => {
    data.value = null;
    alertInfo.value = DEFAULT_ALERT_INFO;
    rows.value = [];
  };

  const thresholdDisplay = computed<ThresholdDisplay>(() => {
    const raw = alertInfo.value.find((item) => item.field === 'threshold')?.values?.[0];
    return (raw ?? {}) as ThresholdDisplay;
  });

  const targetDisplay = computed<AlertTargetItem[]>(() => {
    const values = alertInfo.value.find((item) => item.field === 'target')?.values;
    return (values?.[0] ?? []) as AlertTargetItem[];
  });

  watch(
    () => [props.isShow, userAlertInformationData.value, selectedRuleInfo.value],
    ([isShow, newUserAlertInformationData, newSelectedRuleInfo]) => {
      if (!isShow || !newUserAlertInformationData) return init();
      const {
        alertRuleName = '',
        alertRuleType: type,
        metricConditions,
        eventConditions,
      } = newUserAlertInformationData as AlertRuleDetailV9;

      if (type?.toLowerCase() === 'event') {
        const eventCondition = eventConditions?.[0];
        const { category, variableConditionList, level: severity, targets } = eventCondition ?? {};
        const eventName = variableConditionList?.[0]?.eventName?.[0] ?? '';

        const insightData = ALERT_INSIGHT_DATA[locale.value]?.[category]?.[eventName];
        if (!insightData) return init();

        data.value = insightData;
        rows.value = insightData.mainCauses ?? [];

        const eventTemplate = [
          ...cloneDeep(DEFAULT_ALERT_INFO),
          ...cloneDeep(DEFAULT_ALERT_INFO_EVENT),
        ];
        alertInfo.value = formatAlertInfoItems(eventTemplate, {
          alertName: [alertRuleName],
          target: [
            getTargets({
              targets,
              targetId: (newSelectedRuleInfo as RuleInfoProps)?.alertTargetId,
              category,
            }),
          ],
          type: [getTypeLabel(type, t)],
          eventCode: [eventName],
          severity: severity ? [severity] : [],
        });
        return;
      }

      const metricCondition = metricConditions?.[0];
      const { category, threshold: metricThreshold, targets } = metricCondition ?? {};
      const { alertName = '', conditionType, dataIds } = metricThreshold ?? {};

      const metricId = conditionType === 'formula' ? dataIds?.[0] : alertName;
      if (!metricId) return init();

      const insightData = ALERT_INSIGHT_DATA[locale.value]?.[category]?.[metricId];
      if (!insightData) return init();

      data.value = insightData;
      rows.value = insightData.mainCauses ?? [];

      const { name: metricName, unit: metricUnit } =
        statInfoStore.getStatInfo({
          childCategory: category,
          statId: metricId,
        }) ?? {};

      const metricTemplate = [
        ...cloneDeep(DEFAULT_ALERT_INFO),
        ...cloneDeep(DEFAULT_ALERT_INFO_METRIC),
      ];
      alertInfo.value = formatAlertInfoItems(metricTemplate, {
        alertName: [alertRuleName],
        target: [
          getTargets({
            targets,
            targetId: (newSelectedRuleInfo as RuleInfoProps)?.alertTargetId,
            category,
          }),
        ],
        type: [getTypeLabel(type, t)],
        metric: [metricName],
        threshold: [formatThresholdForDisplay(metricThreshold, metricUnit)] as unknown[],
      });
    },
    { immediate: true, deep: true },
  );

  return {
    t,
    alertData: data,
    alertInfo,
    targets: targetDisplay,
    threshold: thresholdDisplay,
    columns,
    rows,
    option,
  };
};
